import { getCordataWemTriples } from "@biblioteksentralen/bmdb-search-client";
import {
  formatIsbdTitle,
  FormatIsbdTitleOptions,
  isInteractiveWork,
  isLanguageBasedWork,
  isMovingImageWork,
  isMusicalWork,
  Manifestation,
  Work,
} from "@biblioteksentralen/cordata";
import { BookRedia, GameRedia, VideoRedia } from "@biblioteksentralen/icons";
import { getPath, normalizeIsbn } from "@libry-content/common";
import { sift, sort, unique } from "radash";
import { defaultFormatOrder, getFormatOrderIndex } from "./documentTypes";
import {
  createLanguageCodeOrder,
  formatLanguagesList,
  getLanguagesSignature,
  getSortedLanguagesListIndex,
  handleLanguages,
} from "./languages";
import { LCWorkTriple, getContributorsWithPath, getWorkTripleSorter, WorkTripleSorterProps } from "./LCWorkTriple";
import { LibrarySystem } from "@libry-content/types";
import { supportedExternalContentServices } from "./externalContentService";
import { sortByMultiple } from "@biblioteksentralen/utils";
import { Holdings } from "../holdings";
import { VariantSignatures, VariantWithTriples } from "./LCVariant";
import { AtLeastOne, isAtLeastOne } from "@biblioteksentralen/types";
import { Music } from "react-feather";
import { TranslateSanityContent } from "@libry-content/localization";
import { transformGenreName } from "./genres";

export type WorkTripleIdentifier = {
  manifestationId?: string;
  isbn?: string;
};

type Filters = {
  librarySystem?: LibrarySystem;
  manifestationIds?: string[];
};

export const isLCWork = (item: unknown): item is LCWork =>
  !!item && typeof item === "object" && (item as LCWork)?.className === "LCWork";

export type LCWorkParams = { work: Work; representativeManifestationId?: string };

export class LCWork {
  public className = "LCWork" as const;
  private work: Work;
  private representativeManifestationId?: string;

  constructor(params: LCWorkParams) {
    this.work = filterWorkData(params.work);
    this.representativeManifestationId = params.representativeManifestationId;
  }

  // Siden klasseinstanser ikke kan sendes fra server til klient, må vi ha mulighet til å passere parametre istedet som kan instansieres på nytt hos klienten
  public toParams(): LCWorkParams {
    return { work: this.work, representativeManifestationId: this.representativeManifestationId };
  }

  public withRepresentativeManifestationId(representativeManifestationId?: string) {
    return new LCWork({ work: this.work, representativeManifestationId });
  }

  public get isMovie() {
    return isMovingImageWork(this.work);
  }

  public get isInteractive() {
    return isInteractiveWork(this.work);
  }

  public get isMusic() {
    return isMusicalWork(this.work);
  }

  public get isLanguageBasedWork() {
    return isLanguageBasedWork(this.work);
  }

  public get id() {
    return this.work.id;
  }

  public getWorkTriple(workTripleIdentifier: WorkTripleIdentifier) {
    const triples = getCordataWemTriples(this.work);
    const triple = triples.find(({ manifestation }) => {
      if ("manifestationId" in workTripleIdentifier) return manifestation.id === workTripleIdentifier.manifestationId;

      return manifestation.isbn.some((isbn) => normalizeIsbn(isbn) === normalizeIsbn(workTripleIdentifier.isbn));
    });

    if (!triple) return undefined;
    return new LCWorkTriple(triple);
  }

  public get representativeWorkTriple() {
    return this.getWorkTriple({ manifestationId: this.representativeManifestationId });
  }

  public getSortedWorkTriple(props: { representativeLanguages?: string; representativeFormat?: string }) {
    const preferredLanguageCodeOrder = props.representativeLanguages
      ? createLanguageCodeOrder(props.representativeLanguages)
      : undefined;
    const preferredFormatOrder = props.representativeFormat
      ? unique([props.representativeFormat, ...defaultFormatOrder])
      : undefined;

    return this.getWorkTriples({ preferredLanguageCodeOrder, preferredFormatOrder })[0];
  }

  public getWorkTriples(filters?: Filters & WorkTripleSorterProps) {
    const workTriples = getCordataWemTriples(this.work).map((triple) => new LCWorkTriple(triple));
    if (filters)
      return workTriples.filter((triple) => {
        // check if triple is in catalogue if library system is provided
        const isInCatalogue = !filters?.librarySystem || triple.isInCatalogue(filters.librarySystem);
        const manifestationIdIsIncluded =
          !filters?.manifestationIds || filters.manifestationIds?.includes(triple.manifestationId);
        return isInCatalogue && manifestationIdIsIncluded;
      });
    const sortWorkTriples = getWorkTripleSorter();
    return sortWorkTriples(workTriples);
  }

  public get mainContributor() {
    return getContributorsWithPath(sift([this.contributors.find((contributor) => contributor.isMainContributor)]))[0];
  }

  public getWorkCoverImage(filters?: Filters) {
    if (this.representativeWorkTriple?.coverImageUrl) return this.representativeWorkTriple.coverImageUrl;
    return this.getWorkTriples(filters).find((triple) => triple.coverImageUrl)?.coverImageUrl;
  }

  public get contributors() {
    return getContributorsWithPath(this.work.contributors);
  }

  public get workYear() {
    return this.work.workYear;
  }

  public get title() {
    return this.work.title;
  }

  public get languages() {
    return this.work.languages;
  }

  public get languagesSignature() {
    return getLanguagesSignature(this.languages);
  }

  public get path() {
    return getPath(this);
  }

  public get hasPartTitles() {
    return this.title.titleType !== "uniform_title_music" && (!!this.title.partTitle || !!this.title.partNumber);
  }

  public get formattedTitle() {
    return formatIsbdTitle(this.work);
  }

  //get title for presenting work, along with language tag
  public getRepresentativeTitleData(options?: FormatIsbdTitleOptions) {
    if (this.representativeWorkTriple)
      return {
        formattedTitle:
          this.representativeWorkTriple.getFormattedExpressionTitle(options) ??
          this.representativeWorkTriple.getFormattedManifestationTitle(options),
        titleLanguagesSignature: this.representativeWorkTriple.languagesSignature,
      };

    const firstWorkTriple = this.getWorkTriples()[0];
    if (firstWorkTriple)
      return {
        formattedTitle: firstWorkTriple.getFormattedManifestationTitle(options),
        titleLanguagesSignature: firstWorkTriple.languagesSignature,
      };

    return { formattedTitle: this.formattedTitle, titleLanguagesSignature: this.languagesSignature };
  }

  public getGenres(form?: "form" | "nonForm") {
    return unique(
      this.work.genres.filter((genre) => !form || genre.isForm === (form === "form")),
      (genre) => genre.id
    );
  }

  public getTransformedGenres(ts: TranslateSanityContent, form?: "form" | "nonForm") {
    return sift(this.getGenres(form).map((genre) => transformGenreName(ts)(genre)));
  }

  public get ageGroups() {
    return this.work.ageGroups;
  }

  public get subjects() {
    return this.work.subjects;
  }

  public get summary() {
    return this.work.summary;
  }

  public get workTypes() {
    return this.work.workTypes;
  }

  public get workTypeDefiniteNoun() {
    if (this.isMovie) return { nb: "filmen", nn: "filmen" };
    if (this.isInteractive) return { nb: "spillet", nn: "spelet" };
    if (this.isMusic) return { nb: "musikken", nn: "musikken" };
    if (this.isLanguageBasedWork) return { nb: "boka", nn: "boka" };
    return { nb: "verket", nn: "verket" };
  }

  public get ageLimit() {
    return this.work.ageLimit;
  }

  public get type() {
    return this.work.type;
  }

  public getIsbns(filters?: Filters) {
    const workTriples = this.getWorkTriples(filters);
    return workTriples.flatMap(({ isbns }) => isbns);
  }

  public getLanguages(filters?: Filters) {
    const workTriples = this.getWorkTriples(filters);
    const languages = sift(
      unique(
        workTriples.map(({ languages }) => languages).filter((languages) => !!languages),
        (languages) => getLanguagesSignature(languages)
      )
    );
    const languageCodeOrder = createLanguageCodeOrder();
    return sort(languages.map(handleLanguages(languageCodeOrder)), (languagesList) =>
      getSortedLanguagesListIndex(languagesList, languageCodeOrder)
    ).filter((languages) => languages.length);
  }

  public getFormattedLanguages({
    filters,
    capitalizeFirstLetters = "none",
  }: {
    filters?: Filters;
    capitalizeFirstLetters?: "all" | "first" | "none";
  }) {
    return this.getLanguages(filters).map((languageList, index) => {
      const capitalizeFirstLetter =
        capitalizeFirstLetters === "none" ? false : capitalizeFirstLetters === "all" ? true : index === 0;
      return formatLanguagesList(languageList, { capitalizeFirstLetter });
    });
  }

  public getDocumentTypes(filters?: Filters) {
    const workTriples = this.getWorkTriples(filters);
    const uniqueDocumentTypes = sift(
      unique(
        workTriples.filter(({ documentTypeLabel }) => !!documentTypeLabel),
        ({ documentTypeLabel }) => documentTypeLabel!
      ).map(({ documentType }) => documentType)
    );
    return sort(uniqueDocumentTypes, ({ format }) => getFormatOrderIndex(format));
  }

  public getMediaTypeLabels(filters?: Filters) {
    const workTriples = this.getWorkTriples(filters);
    return unique(sift(workTriples.map((triple) => triple.mediaTypeLabel)));
  }

  public getDocumentTypesWithLanguagesSignature = (languagesSignature: string | undefined) => {
    const workTriplesWithLanguagesSignature = this.getWorkTriples().filter(
      (workTriple) => languagesSignature === workTriple.languagesSignature
    );
    const manifestationIds = workTriplesWithLanguagesSignature.map(({ manifestationId }) => manifestationId);
    return sort(this.getDocumentTypes({ manifestationIds }), ({ format }) => getFormatOrderIndex(format));
  };

  public getLanguagesWithDocumentTypeLabel = (documentTypeLabel: string | undefined) => {
    const workTriplesWithDocumentTypeLabel = this.getWorkTriples().filter(
      (workTriple) => documentTypeLabel === workTriple.documentTypeLabel
    );
    const manifestationIds = workTriplesWithDocumentTypeLabel.map(({ manifestationId }) => manifestationId);
    return this.getLanguages({ manifestationIds });
  };

  public getVariantsWithTriples(filters?: Filters): VariantWithTriples[] {
    const workTriples = this.getWorkTriples(filters);
    const variants = unique(
      workTriples.map(({ lcVariant }) => lcVariant),
      ({ signatures }) => signatures.totalSignature
    );
    const sortWorkTriples = getWorkTripleSorter();
    const variantsWithWorkTriples = variants.map((uniqueVariant) => ({
      variant: uniqueVariant,
      workTriples: sortWorkTriples(
        workTriples.filter(
          ({ lcVariant }) => lcVariant.signatures.totalSignature === uniqueVariant.signatures.totalSignature
        )
      ),
    }));
    return sortByMultiple(
      variantsWithWorkTriples,
      ({ variant }) => getFormatOrderIndex(variant.documentType?.format),
      ({ variant }) => getSortedLanguagesListIndex(variant.languages ?? []),
      ({ variant }) =>
        supportedExternalContentServices.findIndex(
          ({ originName }) => originName === variant.externalContentService?.originName
        )
    );
  }

  public getSpecifiedVariantWithTriples(
    signatures: Partial<VariantSignatures>,
    filters?: Filters
  ): VariantWithTriples | undefined {
    return this.getVariantsWithTriples(filters).find(({ variant }) => {
      if (signatures.totalSignature && signatures.totalSignature === variant.signatures.totalSignature) return true;
      const languagesMatch =
        !signatures.languagesSignature || signatures.languagesSignature === variant.signatures.languagesSignature;
      const documentTypeMatch =
        !signatures.documentTypeSignature ||
        signatures.documentTypeSignature === variant.signatures.documentTypeSignature;
      const externalContentServiceMatch =
        !signatures.externalContentServiceOriginSignature ||
        signatures.externalContentServiceOriginSignature === variant.signatures.externalContentServiceOriginSignature;
      return languagesMatch && documentTypeMatch && externalContentServiceMatch;
    });
  }

  public getTriplesPartedByTitle(filters?: Filters): AtLeastOne<LCWorkTriple> | undefined {
    const workTriples = this.getWorkTriples(filters);
    const triplesPartedByTitle = workTriples.filter((workTriple) => !!workTriple.titlePartNumber);
    if (isAtLeastOne(triplesPartedByTitle)) return triplesPartedByTitle;
  }

  public getTriplesPartedBySeries(filters?: Filters): AtLeastOne<LCWorkTriple> | undefined {
    const workTriples = this.getWorkTriples(filters);
    const triplesPartedBySeries = workTriples.filter((workTriple) => !!workTriple.seriesPartNumber);
    return isAtLeastOne(triplesPartedBySeries) ? triplesPartedBySeries : undefined;
  }

  // get triples within work that are differentiated from each other by part number (e.g. "part 1.1" and "part 1.2")
  public getPartedTriples(filters?: Filters) {
    const triplesPartedByTitle = this.getTriplesPartedByTitle(filters) ?? [];
    const moreThanOneTriplesPartedByTitle =
      unique(triplesPartedByTitle ?? [], (workTriple) => workTriple.titlePartNumber!).length > 1;
    const triplesPartedBySeries = this.getTriplesPartedBySeries(filters) ?? [];
    const moreThanOneTriplesPartedBySeries =
      unique(triplesPartedBySeries ?? [], (workTriple) => workTriple.seriesPartNumber!).length > 1;

    return [
      ...(moreThanOneTriplesPartedByTitle ? triplesPartedByTitle : []),
      ...(moreThanOneTriplesPartedBySeries ? triplesPartedBySeries : []),
    ];
  }

  public getNonPartedTriples(filters?: Filters) {
    const partedTriples = this.getPartedTriples(filters);
    return this.getWorkTriples(filters).filter(
      (workTriple) => !partedTriples.find((partedTriple) => partedTriple.manifestationId === workTriple.manifestationId)
    );
  }

  public getTripleBySeriesWorkId(seriesWorkId: string) {
    const workTriples = this.getWorkTriples();
    const workTriple = workTriples.find(({ expressionCollections }) =>
      expressionCollections.find(({ collection }) => collection.workExpressed?.id === seriesWorkId)
    );
    const collectionExpressionEntry = workTriple?.expressionCollections.find(
      ({ collection }) => collection.workExpressed?.id === seriesWorkId
    );
    if (!workTriple || !collectionExpressionEntry) return undefined;
    return { workTriple, collectionExpressionEntry };
  }

  public hasPartedTriples(filters?: Filters) {
    return !!this.getTriplesPartedByTitle(filters) || !!this.getTriplesPartedBySeries(filters);
  }

  // filter away manifestations and expressions from work based on holdings.
  // Use unfiltered work if the library has no holdings or if the updated data from the holdings API is not yet ready.
  // This means that there's a chance the displayed frontend data changes once the updated holdings are obtained, when
  // theholdings according to the data from the search backend deviates from the updated data. If this becomes an
  // issue we could show loaders until the updated data is available. For now we use a blurred version of the image
  // until the data is ready, assuming that a changing image is more confusing than text.

  public filterOnHoldings = (holdings?: Holdings): LCWork => {
    if (!holdings) return this;

    //used to check if a manifestation has at least one item with valid holding state.
    //holding states can be seen here: https://bs-redia.atlassian.net/l/cp/GvJidqrC
    const manifestationHoldingStatusIsValid = ({ libraryCatalogueRecords = [], _origin }: Manifestation) => {
      if (
        supportedExternalContentServices.find(
          (externalContentService) => externalContentService.originName === _origin?.name
        )
      )
        return true;
      return libraryCatalogueRecords.some(({ localRecordId }) => {
        const holdingsDataForRecord = holdings?.[localRecordId];
        if (!holdingsDataForRecord) return false;
        const { reserveId } = holdingsDataForRecord;
        if (
          holdingsDataForRecord.locations.every((location) =>
            //checking if every holding state is invalid
            location.items.every(
              ({ loanableState, availableState }) =>
                (availableState === "available" && loanableState === "not_loanable" && !!reserveId) || //invalid state
                (availableState === "not_available" && loanableState === "not_loanable" && !reserveId) //lost item, deemed invalid state
            )
          )
        )
          return false;
        return true;
      });
    };

    const rawWork = this.work;

    const filteredRawWork = {
      ...rawWork,
      expressions: (rawWork.expressions ?? [])
        .map(({ manifestations = [], aggregateManifestations = [], ...rest }) => ({
          ...rest,
          manifestations: manifestations.filter(manifestationHoldingStatusIsValid),
          aggregateManifestations: aggregateManifestations.filter(manifestationHoldingStatusIsValid),
        }))
        .filter(
          ({ manifestations = [], aggregateManifestations = [] }) =>
            !!manifestations.length || !!aggregateManifestations.length
        ),
    };

    return new LCWork({
      work: filteredRawWork,
      representativeManifestationId: this.representativeManifestationId,
    });
  };

  get illustratedTag() {
    const documentTypes = this.getDocumentTypes();
    if (!documentTypes.some(({ format }) => ["bok", "ebok"].includes(format?.toLowerCase().replace("-", "")))) {
      return undefined;
    }

    const illustratedBookGenres = [
      "1166092", //bildebøker
      "1166093", //pekebøker
      "1103158", //Kunstnerbøker,
      "1453728", //Bilderomaner
    ];
    const comicGenres = [
      "1166096", //Tegneserier
      "1166043", //Grafiske romaner
    ];

    const illustratedWorkGenres = "1203040";

    if (
      this.work.genres?.some((genre) => {
        return illustratedBookGenres.includes(genre.identifiers.bibbiAuthorityId ?? "");
      })
    ) {
      return "Illustrert bok";
    }

    if (
      this.work.genres?.some((genre) => {
        return comicGenres.includes(genre.identifiers.bibbiAuthorityId ?? "");
      })
    ) {
      return "Tegneserie";
    }

    if (
      this.work.genres?.some((genre) => {
        return illustratedWorkGenres === genre.identifiers.bibbiAuthorityId;
      })
    ) {
      return "Illustrert verk";
    }

    return undefined;
  }

  public get placeholderIcon() {
    if (this.isMusic) return Music;
    if (this.isMovie) return VideoRedia;
    if (this.isInteractive) return GameRedia;
    return BookRedia;
  }
}

/**
 * Filter out:
 * - Ebook-manifestations and expressions (temporary solution; can lead to empty works in the frontend)
 */

export const filterWorkData = (work: Work): Work => {
  const expressions = work.expressions
    ?.map(({ manifestations = [], aggregateManifestations = [], ...expression }) => ({
      ...expression,
      manifestations: manifestations.filter(isNotEbook),
      aggregateManifestations: aggregateManifestations.filter(isNotEbook),
    }))
    .filter(
      ({ manifestations = [], aggregateManifestations = [] }) =>
        manifestations.length > 0 || aggregateManifestations.length > 0
    );

  return { ...work, expressions };
};

const isNotEbook = ({ documentType }: Pick<Manifestation, "documentType">) =>
  documentType?.format?.toLowerCase().replace("-", "") !== "ebook";
